import type {
    EstimateInput,
    EstimateResult,
    EstimateSectionResult,
    EstimateItemResult,
    CostSectionWithItems,
    CostItemWithPricing,
    PercentageBaseConfig,
} from '../types';

/**
 * Calculate the total estimate based on input parameters
 */
export function calculateEstimate(
    input: EstimateInput,
    sections: CostSectionWithItems[],
    pricing: Map<string, CostItemWithPricing>
): EstimateResult {
    const sectionResults: EstimateSectionResult[] = [];
    let subtotal = 0;

    // Calculate fixed sections first (they form the base for percentages)
    const fixedSections = sections.filter((s) => s.sectionType === 'FIXED');
    const percentageSections = sections.filter((s) => s.sectionType === 'PERCENTAGE');

    // Process fixed sections
    for (const section of fixedSections) {
        const sectionResult = calculateSectionTotal(
            section,
            input,
            pricing,
            0 // No base needed for fixed
        );
        sectionResults.push(sectionResult);
        subtotal += sectionResult.sectionTotal;
    }

    // Calculate base for percentage sections
    const percentageBase = calculatePercentageBase(
        subtotal,
        sectionResults,
        sections
    );

    // Process percentage sections
    for (const section of percentageSections) {
        const sectionResult = calculateSectionTotal(
            section,
            input,
            pricing,
            percentageBase
        );
        sectionResults.push(sectionResult);
        subtotal += sectionResult.sectionTotal;
    }

    // Sort by display order
    sectionResults.sort((a, b) => {
        const sectionA = sections.find((s) => s.name === a.sectionName);
        const sectionB = sections.find((s) => s.name === b.sectionName);
        return (sectionA?.displayOrder ?? 0) - (sectionB?.displayOrder ?? 0);
    });

    return {
        sections: sectionResults,
        subtotal,
        roomTypeMarkup: 0,
        roomTypeMarkupPercent: 0,
        total: subtotal, // Can add taxes/discounts later
    };
}

/**
 * Calculate total for a single section
 */
export function calculateSectionTotal(
    section: CostSectionWithItems,
    input: EstimateInput,
    pricing: Map<string, CostItemWithPricing>,
    percentageBase: number
): EstimateSectionResult {
    const items: EstimateItemResult[] = [];
    let sectionTotal = 0;

    for (const item of section.items) {
        const itemPricing = pricing.get(item.id);

        // Items must have either explicit pricing OR a default price in the cost item definition
        const hasPricing = itemPricing?.pricing;
        const hasDefaultPricing = item.defaultAmount !== null || item.defaultPercentage !== null || item.defaultUnitPrice !== null;

        if (!hasPricing && !hasDefaultPricing) continue;

        // Check if item should be included
        let shouldInclude = false;

        if (item.exclusiveGroup === 'room_type') {
            shouldInclude = item.name.toLowerCase() === input.roomType.toLowerCase();
        } else if (item.exclusiveGroup === 'condition') {
            shouldInclude = input.selectedConditions.includes(item.id);
        } else if (item.isSelectable === false || item.isSelectable === null) {
            // Non-selectable items are always included
            shouldInclude = true;
        } else if (item.isSelectable === true) {
            // Percentage section selectable items use selectedConditions,
            // fixed section selectable items use selectedItems
            shouldInclude = section.sectionType === 'PERCENTAGE'
                ? input.selectedConditions.includes(item.id)
                : input.selectedItems.includes(item.id);
        }

        if (!shouldInclude) continue;

        // Calculate price
        let unitPrice: number;
        let quantity: number;

        if (section.sectionType === 'FIXED') {
            // Check for room type override
            const override = itemPricing?.pricing?.roomTypeOverrides?.find(
                (o) => o.roomType === input.roomType
            );

            // Priority: Room Override > Procedure Config > Location Default
            unitPrice = override?.amount
                ?? itemPricing?.pricing?.amount
                ?? item.defaultAmount
                ?? 0;

            // For PER_UNIT items, use unitPrice/defaultUnitPrice
            if (item.unit === 'PER_UNIT') {
                unitPrice = itemPricing?.pricing?.unitPrice
                    ?? item.defaultUnitPrice
                    ?? 0;
            }

            // Calculate quantity based on unit
            switch (item.unit) {
                case 'PER_DAY':
                    quantity = input.stayDays;
                    break;
                case 'PER_PROCEDURE':
                    quantity = 1;
                    break;
                case 'PER_UNIT':
                    // Priority: User Input > Procedure Default > 1
                    quantity = input.itemQuantities?.[item.id]
                        ?? itemPricing?.pricing?.defaultQuantity
                        ?? 1;
                    break;
                default:
                    quantity = 1;
            }
        } else {
            // PERCENTAGE type
            const override = itemPricing?.pricing?.roomTypeOverrides?.find(
                (o) => o.roomType === input.roomType
            );
            const percentage = override?.percentage
                ?? itemPricing?.pricing?.percentage
                ?? item.defaultPercentage
                ?? 0;
            unitPrice = (percentageBase * percentage) / 100;
            quantity = 1;
        }

        const lineTotal = unitPrice * quantity;
        sectionTotal += lineTotal;

        items.push({
            id: item.id,
            itemName: item.name,
            quantity,
            unitPrice,
            lineTotal,
        });
    }

    return {
        sectionName: section.name,
        sectionType: section.sectionType,
        items,
        sectionTotal,
    };
}

/**
 * Calculate the base amount for percentage calculations
 */
export function calculatePercentageBase(
    currentSubtotal: number,
    sectionResults: EstimateSectionResult[],
    allSections: CostSectionWithItems[]
): number {
    // Find a percentage section with custom config
    const percentageSection = allSections.find(
        (s) => s.sectionType === 'PERCENTAGE' && s.percentageBaseConfig
    );

    if (!percentageSection?.percentageBaseConfig) {
        // Default: use entire subtotal as base
        return currentSubtotal;
    }

    const config = percentageSection.percentageBaseConfig;

    // Calculate base from specified sections
    let base = 0;
    for (const sectionName of config.includeSections) {
        const result = sectionResults.find((r) => r.sectionName === sectionName);
        if (result) {
            base += result.sectionTotal;
        }
    }

    return base;
}

/**
 * Apply percentage surcharges with optional cumulative logic
 */
export function applyPercentageSurcharges(
    baseAmount: number,
    percentages: number[],
    config?: PercentageBaseConfig
): number {
    if (config?.isCumulative) {
        // Cumulative: each percentage applied to running total
        let total = baseAmount;
        for (const pct of percentages) {
            total += total * (pct / 100);
        }
        return total - baseAmount; // Return only the surcharge amount
    } else {
        // Non-cumulative: all percentages applied to original base
        let totalPercentage = percentages.reduce((sum, pct) => sum + pct, 0);

        // Apply cap if configured
        if (config?.maxPercentage && totalPercentage > config.maxPercentage) {
            totalPercentage = config.maxPercentage;
        }

        return baseAmount * (totalPercentage / 100);
    }
}

/**
 * Format currency for display (Indian Rupees)
 */
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

/**
 * Generate a unique estimate reference number
 * Format: EST/YYYYMMDD/XXX
 */
export function generateReferenceNumber(prefix: string = 'EST'): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;

    // Generate a 3-digit random number
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');

    return `${prefix}/${dateStr}/${random}`;
}
